/**
 * The parts of the stats engine a person drives: edits, packs, and building a
 * stat in the form. Read-only shapes live in `./api`.
 */

import { apiFetch } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { getAdminApiBaseUrl } from "@/lib/api/config";

export type EditKind = "set" | "adjust" | "lock";
export type ReasonType = "calculation_bug" | "ruling" | "data_missing" | "other";

export const REASON_LABEL: Record<ReasonType, string> = {
  calculation_bug: "the engine got it wrong",
  ruling: "a ruling by an official",
  data_missing: "something we never had",
  other: "other",
};

export type EditRow = { targetId: number; field?: string | null; kind: EditKind; value?: unknown };

export type Edit = {
  id: number;
  batchId: string;
  targetTable: string;
  targetId: number;
  field: string | null;
  kind: EditKind;
  value: unknown;
  reasonType: ReasonType;
  note: string;
  author: string;
  status: "draft" | "published" | "released";
  createdAt: string;
  publishedAt: string | null;
  releasedAt: string | null;
  releasedBy: string | null;
  releaseReason: string | null;
};

export type Change = {
  changedAt: string;
  field: string;
  old: unknown;
  new: unknown;
  cause: "run" | "edit" | "restore" | "publish";
  subjectId: string;
  runId: number | null;
  editBatchId: string | null;
};

export type Pack = {
  id: string;
  key: string;
  version: number;
  status: string;
  name: string;
  description: string;
  ownerRef: string;
  blanks: Record<string, string>;
  items: Array<{ definition_key: string; definition_version: number; tier_override: string | null }>;
};

export type Mark = { rule: string; ok: boolean; warn: boolean; note: string };

/** One word the Add-a-stat form may offer, and what it means. */
export type Word = { value: string; note: string; kind: "fact" | "column" | "segment" | "stat" };

/** Somewhere a draft stat can be previewed against. */
export type PreviewScope = { kind: string; ref: string; label: string; fixtures: number };

/** What a sport's lines can be asked about, for the form's autocomplete. */
export type Vocabulary = {
  sport: string;
  /** Numbers a match line may carry. */
  facts: Word[];
  /** Stamps every line carries: team, opponent, venue, format, date, segment, rank. */
  columns: Word[];
  /** Parts of a match this sport has actually written lines for. */
  segments: Word[];
  /** Stats already declared for the sport, for one that reads a rung down. */
  stats: Word[];
  /** Competitions with fixtures under them, biggest first, then the whole
   *  sport. Saving is gated on a clean preview, so a form with nowhere to run
   *  is a form nobody can finish. */
  scopes: PreviewScope[];
};

export type PreviewResult = {
  ok: boolean;
  rows: Array<Record<string, unknown>>;
  milliseconds: number;
  matches: number;
  sameTwice: boolean;
  marks: Mark[];
  sql: string;
  error: string;
};

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(`${getAdminApiBaseUrl()}/stats-engine${path}`, {
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === "string") message = body.detail;
    } catch {
      /* keep the status line */
    }
    throw new ApiError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const post = <T>(path: string, body?: unknown) =>
  call<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

export const draftEdits = (payload: {
  targetTable: string;
  rows: EditRow[];
  reasonType: ReasonType;
  note: string;
  author: string;
}) => post<{ batchId: string; rows: number }>("/edits", payload);

export const publishEdits = (batchId: string, by: string) =>
  post<{ published: number; rows: number; changes: number }>(
    `/edits/${batchId}/publish?by=${encodeURIComponent(by)}`,
  );

export const releaseEdits = (batchId: string, by: string) =>
  post<{ released: number; rows: number }>(`/edits/${batchId}/release?by=${encodeURIComponent(by)}`);

export const listEdits = (targetId: number) =>
  call<Edit[]>(`/edits?targetId=${targetId}`);

export const listChanges = (key: string, scopeKind: string, scopeRef: string, subjectId?: string) =>
  call<Change[]>(
    `/changes?key=${encodeURIComponent(key)}&scopeKind=${scopeKind}&scopeRef=${encodeURIComponent(scopeRef)}` +
      (subjectId ? `&subjectId=${subjectId}` : ""),
  );

export const listPacks = (sport: string) => call<Pack[]>(`/packs?sport=${encodeURIComponent(sport)}`);

export const attachPack = (payload: {
  targetKind: string;
  targetRef: string;
  packKey?: string;
  definitionKey?: string;
  settings?: Record<string, unknown>;
}) => post<{ id: string }>("/packs/attach", payload);

export const loadVocabulary = (sport: string) =>
  call<Vocabulary>(`/authoring/vocabulary?sport=${encodeURIComponent(sport)}`);

export const previewStat = (spec: Record<string, unknown>, scopeKind: string, scopeRef: string) =>
  post<PreviewResult>("/authoring/preview", { spec, scopeKind, scopeRef });

export const saveStat = (payload: {
  key: string;
  name: string;
  sportCode: string;
  scopeKind: string;
  spec: Record<string, unknown>;
  description?: string;
  tier?: string;
  edit?: string;
}) => post<{ id: string; key: string; version: number; status: string }>("/authoring/save", payload);
